import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import getSymbolFromCurrency from 'currency-symbol-map'

import { OfferPaymentFirstStepForm } from 'Modules/purchase/components/OfferPaymentForm/OfferPaymentFirstStepForm'
import { OfferPaymentSecondStepForm } from 'Modules/purchase/components/OfferPaymentForm/OfferPaymentSecondStepForm'
import { OfferStatus } from 'Modules/purchase/components/OfferStatus/OfferStatus'
import { OfferSummary } from 'Modules/purchase/components/OfferSummary/OfferSummary'
import { OfferError } from 'Modules/purchase/pages/OfferError/OfferError.page'

import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { setRouteTitle } from 'Portal/src/router/Router.utils'
import { fetchFromPublicApiUsingPartialPath } from 'Portal/src/utilities'

import type {
  OfferDetailsResponse,
  StripeInvoiceData,
} from './PurchaseDevice.types'

export const PurchaseDevice = () => {
  const [invoiceData, setInvoiceData] = useState<StripeInvoiceData | null>(null)
  const [offerDetails, setOfferDetails] = useState<OfferDetailsResponse | null>(
    null,
  )
  const [isError, setIsError] = useState(false)

  const intl = useIntl()
  const { uuid = '' } = useParams<{ uuid: string }>()
  const navigate = useNavigate()

  // Fetch offer details
  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        const response =
          await fetchFromPublicApiUsingPartialPath<OfferDetailsResponse>(
            `/payment-link/${uuid}`,
            { method: 'POST' },
          )

        if (response.recreate_offer) {
          navigate('/offer-recreated', { replace: true })

          return
        }

        if (response.error) {
          setIsError(true)

          return
        }

        if (response?.invoice_data?.client_secret) {
          setInvoiceData(response.invoice_data)
        }

        setOfferDetails(response)
      } catch (error) {
        setIsError(true)
      }
    }

    fetchOfferDetails()
  }, [navigate, uuid])

  // Set page title
  useEffect(() => {
    setRouteTitle(intl.formatMessage({ id: 'Purchase_device' }))
  }, [intl])

  // Get currency symbol
  const currencySymbol = useMemo(() => {
    if (!offerDetails?.currency) {
      return null
    }

    return getSymbolFromCurrency(offerDetails.currency.toUpperCase())
  }, [offerDetails])

  // Handle error state
  if (isError) {
    return <OfferError />
  }

  // Handle non-accepted offer status
  if (offerDetails && offerDetails.offer_status !== 'accepted') {
    return <OfferStatus offerStatus={offerDetails.offer_status} />
  }

  // Handle loading state
  if (!offerDetails || !currencySymbol) {
    return <PageLoadingState />
  }

  // Payment form visibility check
  const showPaymentForm = !!(
    offerDetails.payable_price &&
    offerDetails.payable_price_in_cents &&
    offerDetails.currency
  )

  const missingClientSecret = !invoiceData?.client_secret

  return (
    <div className="PurchaseDevice">
      <div className="PurchaseDevice-offerSummary d-flex w-100 p-9 justify-content-center">
        <OfferSummary
          currencySymbol={currencySymbol}
          itemName={offerDetails.device_name}
          itemPrice={offerDetails.net_price}
          subTotal={offerDetails.net_price}
          tax={offerDetails.tax_amount}
          total={offerDetails.payable_price}
        />
      </div>

      {showPaymentForm && (
        <div className="PurchaseDevice-offerPayment d-flex w-100 p-9 justify-content-center bg-white">
          {missingClientSecret ? (
            <OfferPaymentFirstStepForm
              amount={offerDetails.payable_price_in_cents}
              billingAddress={invoiceData?.address}
              billingCountry={offerDetails.billing_country.toUpperCase()}
              billingName={
                invoiceData?.customer_name || offerDetails.employee_full_name
              }
              currency={offerDetails.currency.toLowerCase()}
              email={invoiceData?.customer_email || offerDetails.email}
              setInvoiceData={setInvoiceData}
            />
          ) : (
            <OfferPaymentSecondStepForm
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              uuid={uuid}
            />
          )}
        </div>
      )}
    </div>
  )
}
